// This is a pie chart wrapped in a donut chart. One represents crops, the other varieties. 
// We should be able to determine percents visually, but a global value would be nice. There should also be a legend.
// Clicking on one will change the "zoom level"

import React, { useState } from 'react';
import Pie, { ProvidedProps, PieArcDatum } from '@visx/shape/lib/shapes/Pie';

import { scaleOrdinal } from '@visx/scale';
import { Group } from '@visx/group';
import {stringBrightness, hex2rgb, darken, callback} from '../../helpers.js';
import colourspace from 'color-space';

import './VarietySuperPieChart.scss';


const defaultMargin = { top: 20, right: 20, bottom: 20, left: 20 };

export default ({
  width,
  height,
  margin = defaultMargin,
  animate = true,
  data,
  onSelectionChange
}) => {
	
	const [selectedCrop, setSelectedCrop] = useState(null);
	const [selectedVariety, setSelectedVariety] = useState(null);

	const [hoverCrop, setHoverCrop] = useState(null);
	const [hoverVariety, setHoverVariety] = useState(null);

	  if (width < 10) return null;

	  const innerWidth = width - margin.left - margin.right;
	  const innerHeight = height - margin.top - margin.bottom;
	  const radius = Math.min(innerWidth, innerHeight) / 2;
	  const centerY = innerHeight / 2;
	  const centerX = innerWidth / 2;
	  const donutThickness = 80;

	const sum_acres = (crop)  => {
		return data[crop].reduce((a, c) => a + parseFloat(c['acres']), 0);
		/*let total = 0;
		for(let i=0; i < data[crop].length; i++)
			total += parseFloat(data[crop][i]['acres']);
		}
		console.log("Summing acres for", o);
		return 0;*/
	}
	
	const acres = (o)=> parseFloat(o['acres']);

	let flattened = [];
	for(let crop in data) {
		for(let i=0; i < data[crop].length; i++) {
			let o = data[crop][i];
			flattened.push(o);
		}
	}

	const getCropColour = (o) => {
		return data[o][0]['colour'] || '#666666';
	}

	const getVarietyColour = (o) => {
		// console.log('gvc', o);
		return stringBrightness(o['colour'] || '#666666', o.VarietyName);
		//return data[o][0]['colour'] || '#666666';
	}
	
	const getVarietyLabel = (o) => {
		return /*o.data.CropName + ': ' +*/o.data.VarietyName;
	}
	
	const sortCrops = (a,b) => {
		if (a > b)
			return 1;
		if (a < b)
			return -1;
		return 0;
	}

	
	const sortVarieties = (a,b) => {
		if (a['CropName'] > b['CropName'])
			return 1;
		if (a['CropName'] < b['CropName'])
			return -1;
		// console.log(a);
		// What if i sort this step by luminosity instead?
		if (a['colour'] == null || b['colour'] == null) {
			if (a['VarietyName'] > b['VarietyName'])
				return 1;
			if (a['VarietyName'] < b['VarietyName'])
				return -1;
		} else {
			if (colourspace.rgb.hsl(hex2rgb(a['colour']))[2] > colourspace.rgb.hsl(hex2rgb(b['colour']))[2])
				return 1;
			if (colourspace.rgb.hsl(hex2rgb(a['colour']))[2] < colourspace.rgb.hsl(hex2rgb(b['colour']))[2])
				return -1;
		}
		return 0;
	}


	const mouseover = (o) => {
		if (typeof o === 'string' || o instanceof String) { // It's a crop
			setHoverCrop(o);
			setHoverVariety(null);
		} else {
			setHoverCrop(null);
			setHoverVariety(o['VarietyName']);
		}
	}

	const mouseout = (o) => {
		if (typeof o === 'string' || o instanceof String) { // It's a crop
			setHoverCrop(null);
		} else {
			setHoverVariety(null);
		}
	}



	const click = (o) => {
		if (typeof o === 'string' || o instanceof String) { // It's a crop
			changeSelection(o, null);
		} else {
			changeSelection(o['CropName'], o['VarietyName']);
		}
	}
	
	const changeSelection = (crop, variety) => {
		setSelectedCrop(crop);
		setSelectedVariety(variety);
		callback(onSelectionChange, [crop, variety]);
	}

	// Create the legend
	let legend = [];
	let totalAcres = 0;
	// If selectedCrop == null: show crops (plus number of acres)
	if (selectedCrop == null) {
		const crops = Object.keys(data).sort(sortCrops);
		for(let i=0; i < crops.length; i++) {
			let c = crops[i]; // for the closure
			legend.push(<li key={c} onClick={()=>click(c)} onMouseOver={()=>mouseover(c)} onMouseOut={()=>mouseout(c)}><div className="colour-block" style={{backgroundColor: getCropColour(c)}}></div> {c} ({sum_acres(c).toLocaleString()} acres)</li>);
			totalAcres += sum_acres(c);
		}
	} else {
		//If selectedVariety == null: show varieties (plus number of acres)
		if (selectedVariety == null) {
			let d = data[selectedCrop].sort(sortVarieties);
			for(let i=0; i < d.length; i++) {
				legend.push(<li key={d[i]['VarietyName']}  onClick={()=>click(d[i])} onMouseOver={()=>mouseover(d[i])} onMouseOut={()=>mouseout(d[i])}><div className="colour-block" style={{backgroundColor: getVarietyColour(d[i])}}></div> {d[i]['VarietyName']} ({parseFloat(d[i]['acres']).toLocaleString()} acres)</li>);
				totalAcres += parseFloat(d[i]['acres']);
			}
		} else {
			//show only that variety
			let d = null;
			for(let i=0; i < data[selectedCrop].length; i++) {
				if (data[selectedCrop][i]['VarietyName'] == selectedVariety) {
					d = data[selectedCrop][i];
					break;
				}
			}
			legend.push(<li key={d['VarietyName']} onClick={()=>click(d)} onMouseOver={()=>mouseover(d)} onMouseOut={()=>mouseout(d)}><div className="colour-block" style={{backgroundColor: getVarietyColour(d)}}></div> {d['VarietyName']} ({parseFloat(d['acres']).toLocaleString()} acres)</li>);
			totalAcres += parseFloat(d['acres']);
		}
	}

	// Create summary: this should be selected or hovered in that order 
	// And should show crop/ variety, total acres, and total percent

	// Show breadcrumbs: All crops : selectedCrop : selected Variety
	let breadcrumbs = [<li key="all" onClick={()=>changeSelection(null, null)}>All crops</li>];
	if (selectedCrop !== null)
		breadcrumbs.push(<li key={selectedCrop} onClick={()=>changeSelection(selectedCrop, null)}>{selectedCrop}</li>);
	// If you select a variety, also select a crop
	if (selectedVariety !== null) {
		let associatedCrop = null;
		for(let i=0; i < flattened.length; i++) {
			if (flattened[i]['VarietyName'] == selectedVariety) {
				associatedCrop = flattened[i]['CropName'];
				break;
			}
		}
		breadcrumbs.push(<li key={selectedVariety} onClick={()=>changeSelection(associatedCrop, selectedVariety)}>{selectedVariety}</li>);
	}
	
	return <div className="superpie">
		<ol className="breadcrumbs">{breadcrumbs}</ol>
		<svg width="100%" height="100%" viewBox={"0 0 " + width + " " + height}
		preserveAspectRatio="xMidYMin slice"
		>
		 <Group top={centerY + margin.top} left={centerX + margin.left}>
			<Pie
			  data={
				selectedCrop ? Object.keys(data).filter((label) => label === selectedCrop) : Object.keys(data)
			  }
			  pieValue={sum_acres}
			  pieSort={(a,b)=>sortCrops(a,b)}
			  outerRadius={radius}
			  innerRadius={radius - donutThickness}
			  cornerRadius={3}
			  padAngle={0.005}
			>
			 {(pie) => (
				<AnimatedPie
				  {...pie}
				  animate={animate}
				  getKey={(arc) => arc.data}
				  getColor={(arc) => getCropColour(arc.data)}
				  highlighted={(arc)=>arc.data == hoverCrop}
				  onMouseOver={(arc)=>mouseover(arc)}
				  onMouseOut={(arc)=>mouseout(arc)}
				  onClick={(arc)=>click(arc)}
				/>
			  )}
			</Pie>
			<Pie
			  data={
				flattened.filter((o) => {
					if (selectedCrop !== null && o['CropName'] != selectedCrop) return false;
					if (selectedVariety !== null && o['VarietyName'] != selectedVariety) return false;
					return true;
				})
			  }
			  pieValue={acres}
			  pieSort={(a,b)=>sortVarieties(a,b)}
			  outerRadius={radius - donutThickness - 4}
			>
			 {(pie) => (
				<AnimatedPie
				  {...pie}
				  animate={animate}
				  getKey={(arc) => getVarietyLabel(arc)}
				  getColor={(arc) => getVarietyColour(arc.data)}
				  highlighted={(arc)=>arc.data['VarietyName'] == hoverVariety}
				  onMouseOver={mouseover}
				  onMouseOut={mouseout}
				  onClick={(arc)=>click(arc)}
				/>
			  )}
			</Pie>
		  </Group>
		</svg>
		<div className="legend">
			<ul>{legend}</ul>
			<p>Covering {totalAcres.toLocaleString()} acres.</p>
		</div>
	</div>;
}




function AnimatedPie({
  animate,
  arcs,
  path,
  getKey,
  getColor,
  onClickDatum,
  highlighted,
  onMouseOver,
  onMouseOut,
  onClick
}) {
  
	
	
	let chunks = [];
	for(let i=0; i < arcs.length; i++) {
		const key = getKey(arcs[i]);
		const [centroidX, centroidY] = path.centroid(arcs[i]);
		const hasSpaceForLabel = arcs[i].endAngle - arcs[i].startAngle >= 0.3;
		const sa = arcs[i].startAngle;
		const ea = arcs[i].endAngle;
		chunks.push(<g key={key}
			onMouseOver={()=>onMouseOver(arcs[i].data)}
			onMouseOut={()=>onMouseOut(arcs[i].data)}
		>
			<path d={path(arcs[i])}
				fill={getColor(arcs[i])}
				onClick={() => onClick(arcs[i].data)}
				onTouchStart={() => onClick(arcs[i].data)}
				stroke={darken(getColor(arcs[i]), 0.3)}
				strokeWidth={highlighted(arcs[i]) ? "5" : "0"}
				strokeOpacity="0.9"
			/>
			{hasSpaceForLabel && (
				  <g style={{ opacity: 1}}>
					<text
					  fill="white"
					  x={centroidX}
					  y={centroidY}
					  dy=".33em"
					  fontSize={14}
					  textAnchor="middle"
					  pointerEvents="none"
					>
					  {key}
					</text>
					<title>{key}</title>
				  </g>
				)}
			  </g>);
	}
	return chunks;
}